import { BaseService, BaseServiceConstructor } from 'api/services/base.service';
import { plainToInstance } from 'class-transformer';
import { Property, ServiceLineMaterial } from 'types/models';
import { PropertyDetails } from 'types/models/inventory/PropertyDetails';
import DataTableRecord from 'types/models/DataTableRecord';
import { UpdateServiceLinesRequest } from 'types/requests/UpdateServiceLineRequest';
import { UpdatePropertyDetailsRequest } from 'types/requests/UpdatePropertyDetailsRequest';
import { PublicUserPropertyDTO } from 'types/models/public/PublicUserProperty';
import { DeepPartial, PublicUserContactProperty } from '@cast/leadcast-types';
import { PropertyCommunicationEvent, CustomerContactProperty } from '@cast/leadcast-types';

export class PropertyService extends BaseService {
  constructor({ serviceUrls, token, tenantName }: BaseServiceConstructor) {
    super({ serviceUrls, token, tenantName });
  }

  async getProperty(externalId: Property['external_id']): Promise<Property> {
    const { data } = await this.publicHttpClient.get(`/properties/${externalId}`, {
      withCredentials: false,
    });
    return data[0];
  }

  async retireServiceLineMaterial(
    propertyId: Property['id'],
    serviceLineMaterialId: ServiceLineMaterial['id']
  ): Promise<PropertyDetails> {
    const { data } = await this.publicHttpClient.delete<PropertyDetails>(
      `/properties/${propertyId}/serviceLineMaterials/${serviceLineMaterialId}`,
      undefined,
      { withCredentials: false }
    );
    return plainToInstance(PropertyDetails, data);
  }

  async getPropertyDetails(propertyId: Property['id']): Promise<PropertyDetails> {
    const { data } = await this.publicHttpClient.get<PropertyDetails>(
      `/properties/${propertyId}/details`,
      {
        withCredentials: false,
      }
    );
    return plainToInstance(PropertyDetails, data);
  }

  async updateRightOfEntryFormStatusById(
    rightOfEntryFormId: string,
    status: string,
    reason: string
  ): Promise<void> {
    await this.publicHttpClient.put<void>(
      `/properties/rightOfEntry/${rightOfEntryFormId}/${status}`,
      { reason },
      {
        withCredentials: false,
      }
    );
  }

  async updatePropertyDetails(
    propertyId: Property['id'],
    updatedRecord: UpdatePropertyDetailsRequest
  ): Promise<PropertyDetails> {
    const { data } = await this.publicHttpClient.put<PropertyDetails, UpdatePropertyDetailsRequest>(
      `/properties/${propertyId}/edit`,
      updatedRecord,
      {
        withCredentials: false,
      }
    );
    return plainToInstance(PropertyDetails, data);
  }

  async updateServiceLinesForProperty(
    propertyId: Property['id'],
    body: UpdateServiceLinesRequest
  ): Promise<PropertyDetails> {
    const { data } = await this.publicHttpClient.put<PropertyDetails>(
      `/properties/${propertyId}/serviceLines`,
      body,
      {
        withCredentials: false,
      }
    );
    return plainToInstance(PropertyDetails, data);
  }

  async getDataTableRecord(propertyId: Property['id']): Promise<DataTableRecord> {
    const { data } = await this.publicHttpClient.get<DataTableRecord>(
      `/properties/datatable/${propertyId}`,
      {
        withCredentials: false,
      }
    );
    return data;
  }

  async deleteProperty(
    propertyExternalId: string,
    payload: {
      statusCode: string;
    }
  ): Promise<void> {
    await this.httpClient.delete(`/Properties/${propertyExternalId}`, payload);
  }

  async getPublicUsersByPropertyId(propertyId: string): Promise<PublicUserPropertyDTO[]> {
    const { data } = await this.publicHttpClient.get<PublicUserPropertyDTO[]>(
      `/properties/${propertyId}/publicUsers`,
      {
        withCredentials: false,
      }
    );
    return data;
  }

  async updatePublicUserProperty(
    updatedValues: DeepPartial<PublicUserPropertyDTO> & { id: PublicUserPropertyDTO['id'] }
  ): Promise<void> {
    await this.publicHttpClient.put<void>(
      `/properties/publicUserProperties/${updatedValues.id}`,
      { updatedValues },
      {
        withCredentials: false,
      }
    );
  }

  async createCustomerContact(
    propertyId: string,
    customerContactProperty: DeepPartial<CustomerContactProperty>
  ): Promise<CustomerContactProperty> {
    const { data } = await this.publicHttpClient.post<CustomerContactProperty>(
      `/properties/${propertyId}/customerContacts`,
      { customerContactProperty },
      {
        withCredentials: false,
      }
    );
    return data;
  }

  async getCustomerContactsByPropertyId(propertyId: string): Promise<CustomerContactProperty[]> {
    const { data } = await this.publicHttpClient.get<CustomerContactProperty[]>(
      `/properties/${propertyId}/customerContacts`,
      {
        withCredentials: false,
      }
    );
    return data;
  }

  async getContactsAndPublicUsers(
    page: number,
    limit = 25,
    filters = {}
  ): Promise<PublicUserContactProperty[]> {
    let params: any = {
      ...filters,
      'page[number]': page,
      'page[size]': limit,
    };

    const { data } = await this.publicHttpClient.get<PublicUserContactProperty[]>(
      `/properties/customerContacts`,
      {
        params,
        paramsSerializer: { indexes: null },
        withCredentials: false,
      }
    );

    return data;
  }

  async updateCustomerContact(
    updatedValues: DeepPartial<CustomerContactProperty> & {
      id: CustomerContactProperty['id'];
    }
  ): Promise<void> {
    await this.publicHttpClient.put<void>(
      `/properties/customerContacts/${updatedValues.id}`,
      { updatedValues },
      {
        withCredentials: false,
      }
    );
  }

  async getCommunicationEventsByPropertyId(
    propertyId: string
  ): Promise<PropertyCommunicationEvent[]> {
    const { data } = await this.publicHttpClient.get<PropertyCommunicationEvent[]>(
      `/properties/${propertyId}/communications`,
      {
        withCredentials: false,
      }
    );
    return data;
  }
}
